import Box from '@mui/material/Box/Box';
import React from 'react';

import { selectionStore } from '../../PdfSplitEditor/utils/SelectionStore';
import { PdfGridSquare } from './PdfGridSquare';

type CellProps<T> = {
  rowIndex: number;
  columnIndex: number;
  style: React.CSSProperties;
} & T;

interface CellPropsExtras {
  pageNumber: number;
  isCurrentPage: boolean;
  isGrouped: boolean;
  groupNumber: number;
  positionInGroup: number;
  groupSize: number;
  groupColor: string;
  groupBorderColor: string;
  onRemovePage?: (pageNum: number) => void;
  onClick: (pageNumber: number, e: React.MouseEvent) => void;
}

const GridCellComponent = ({
  style,
  pageNumber,
  groupBorderColor,
  groupColor,
  groupNumber,
  groupSize,
  isCurrentPage,
  isGrouped,
  onClick,
  positionInGroup,
  onRemovePage,
}: CellProps<CellPropsExtras>) => {
  /**
   * This pattern of subscribing to an external store for the selected state
   * is needed to avoid expensive re-renders of PDF pages when the selectedPages state of the parent component changes
   */
  const [isSelected, setIsSelected] = React.useState(() =>
    selectionStore.has(pageNumber)
  );

  React.useEffect(() => {
    const unsub = selectionStore.subscribe(() => {
      const selected = selectionStore.has(pageNumber);
      setIsSelected((prev) => (prev !== selected ? selected : prev)); // only update if changed
    });
    return () => {
      unsub();
    };
  }, [pageNumber]);

  const handleClick = (pageNum: number, e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) {
      selectionStore.toggle(pageNum); // triggers only this cell to rerender
    }
    onClick(pageNum, e);
  };

  const pageEl = (
    <PdfGridSquare
      key={`page_${pageNumber}`}
      pageNumber={pageNumber}
      isCurrentPage={isCurrentPage}
      isSelected={isSelected}
      isGrouped={isGrouped}
      groupNumber={groupNumber}
      positionInGroup={positionInGroup}
      groupSize={groupSize}
      groupColor={groupColor}
      groupBorderColor={groupBorderColor}
      onRemovePage={onRemovePage}
      onClick={handleClick}
    />
  );

  return (
    <Box style={style} sx={{ display: 'flex', justifyContent: 'center' }}>
      {pageEl}
    </Box>
  );
};

export { GridCellComponent };
export type { CellProps, CellPropsExtras };
